import {
  type MutateOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import { gqlClient } from '@/lib';
import type { QueryError } from '@/utils/errors';
import { createExperienceQueryKey } from '@/utils/queryKeys';
import type {
  RequestFeedAccessMutation,
  RequestFeedAccessMutationVariables,
} from './useRequestFeedAccess.generated';

// Takes over as the source's access subject, then re-checks. A source is held on
// behalf of whoever added it, so the caller has to claim it before asking for
// anything: granting access to the caller while someone else still holds the row
// would change nothing. If the caller can already read the origin the events come
// straight back and no mail is sent; otherwise the origin's owner is emailed.
const query = gql`
  mutation RequestFeedAccess($input: RequestFeedAccessInput!) {
    requestFeedAccess(input: $input) {
      id
      accessRevokedAt
    }
  }
`;

export const useRequestFeedAccess = () => {
  const queryClient = useQueryClient();
  const { experience } = usePageExperienceContext();

  const { mutate, ...rest } = useMutation<
    RequestFeedAccessMutation,
    QueryError,
    RequestFeedAccessMutationVariables
  >({
    mutationFn: (variables) =>
      gqlClient.request<
        RequestFeedAccessMutation,
        RequestFeedAccessMutationVariables
      >(query, variables),
    // The events key nests under the experience key, so one invalidation brings
    // back both the feed's flag and the events it starts delivering again.
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: createExperienceQueryKey(experience.id),
      }),
  });

  const requestFeedAccess = (
    feedId: string,
    options?: MutateOptions<
      RequestFeedAccessMutation,
      QueryError,
      RequestFeedAccessMutationVariables
    >
  ) => mutate({ input: { experienceId: experience.id, feedId } }, options);

  return { requestFeedAccess, ...rest };
};
